import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LayoutDashboard, Mail, Users, Database, LogOut, FileText, Flag, Megaphone, ShieldCheck } from 'lucide-react';
import { AuthContext } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import AdminAccessControl from '@/components/admin/AdminAccessControl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminNavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";

  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showAccessControl, setShowAccessControl] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [bTitle, setBTitle] = useState('');
  const [bContent, setBContent] = useState('');
  const [bPriority, setBPriority] = useState('medium');
  const [sending, setSending] = useState(false);
  
  // Poll pending-signups count so the Access button shows a red badge with
  // the number of accounts awaiting approval. Refresh whenever the modal
  // opens or closes so admins see the count update immediately after
  // clicking Approve/Reject.
  useEffect(() => {
    // Only admins hit /admin/pending-users — avoid speculative 403 traffic
    // (and log noise) from player sessions that briefly mount this nav.
    if (!isAdmin) return undefined;
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/pending-users`, { withCredentials: true });
        if (!cancelled) setPendingCount(res.data?.count ?? 0);
      } catch {
        // Silent — session may have expired between polls
      }
    };
    fetchPending();
    const t = setInterval(fetchPending, 60000);  // gentle 60s refresh
    return () => { cancelled = true; clearInterval(t); };
  }, [isAdmin, showAccessControl]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleBroadcast = async () => {
    if (!bTitle.trim() || !bContent.trim()) return toast.error('Title and content required');
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/messages`,
        { message_type: 'general', title: bTitle, content: bContent, priority: bPriority },
        { withCredentials: true }
      );
      toast.success('Broadcast sent to all players');
      setShowBroadcast(false);
      setBTitle('');
      setBContent('');
      setBPriority('medium');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', testid: 'nav-dashboard' },
    { path: '/admin/send-email', icon: Mail, label: 'Email Players', testid: 'nav-email' },
    { path: '/admin/players', icon: Users, label: 'Players', testid: 'nav-players' },
    { path: '/admin/courses', icon: Flag, label: 'Courses', testid: 'nav-courses' },
    { path: '/admin/utilities', icon: Database, label: 'Utilities', testid: 'nav-utilities' },
    { path: '/admin/legal', icon: FileText, label: 'Legal Pages', testid: 'nav-legal' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className="bg-white border-b border-line shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo/Brand */}
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Golf Eclectic" className="w-8 h-8" />
              <span className="font-bold text-ink text-lg">Golf Eclectic Admin</span>
            </div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    data-testid={item.testid}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      active ? 'bg-accent-tint text-ink' : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Right side: Broadcast + Access Control + Logout */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setShowAccessControl(true)}
                data-testid="nav-access-control-btn"
                className="relative hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-ink border border-line hover:bg-app transition-colors"
                title="Signup access code + pending approvals"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Access</span>
                {pendingCount > 0 && (
                  <span
                    data-testid="access-pending-badge"
                    className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] font-bold"
                    title={`${pendingCount} awaiting approval`}
                  >
                    {pendingCount > 99 ? "99+" : pendingCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowBroadcast(true)}
                data-testid="nav-broadcast-btn"
                className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-accent-deep border border-accent/30 bg-accent-tint hover:bg-accent-tint-2 transition-colors"
                title="Broadcast a message to all players"
              >
                <Megaphone className="w-4 h-4" />
                <span>Broadcast</span>
              </button>
              <button
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-ink-muted hover:bg-red-50 hover:text-red-600 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden pb-3">
            <div className="flex flex-wrap gap-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    data-testid={`${item.testid}-mobile`}
                    className={`flex items-center gap-1 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                      active ? 'bg-accent-tint text-ink' : 'text-ink-muted hover:bg-surface-2'
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
              <button
                onClick={() => setShowAccessControl(true)}
                data-testid="nav-access-control-btn-mobile"
                className="relative flex items-center gap-1 px-2 py-1.5 rounded text-xs font-medium text-ink border border-line"
              >
                <ShieldCheck className="w-3 h-3" />
                <span>Access</span>
                {pendingCount > 0 && (
                  <span
                    data-testid="access-pending-badge-mobile"
                    className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold"
                  >
                    {pendingCount > 9 ? "9+" : pendingCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowBroadcast(true)}
                data-testid="nav-broadcast-btn-mobile"
                className="flex items-center gap-1 px-2 py-1.5 rounded text-xs font-medium text-accent-deep border border-accent/30 bg-accent-tint"
              >
                <Megaphone className="w-3 h-3" />
                <span>Broadcast</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Broadcast Dialog */}
      <Dialog open={showBroadcast} onOpenChange={setShowBroadcast}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-accent" /> Broadcast to all players
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-accent-tint-2 border border-accent-tint text-[13px] text-accent-deep">
              Sent to <b>all players</b> system-wide and shown on their dashboard.
            </div>
            <div>
              <label className="text-[12px] font-semibold tracking-wider uppercase text-ink-muted">Title</label>
              <input
                value={bTitle}
                onChange={(e) => setBTitle(e.target.value)}
                placeholder="Subject line"
                data-testid="broadcast-title"
                className="mt-1.5 w-full px-3 py-2 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent text-sm"
                maxLength={100}
              />
            </div>
            <div>
              <label className="text-[12px] font-semibold tracking-wider uppercase text-ink-muted">Content</label>
              <textarea
                value={bContent}
                onChange={(e) => setBContent(e.target.value)}
                rows={6}
                placeholder="Message body…"
                data-testid="broadcast-content"
                className="mt-1.5 w-full px-3 py-2 border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent text-sm"
              />
            </div>
            <div>
              <label className="text-[12px] font-semibold tracking-wider uppercase text-ink-muted">Priority</label>
              <select
                value={bPriority}
                onChange={(e) => setBPriority(e.target.value)}
                data-testid="broadcast-priority"
                className="mt-1.5 w-full px-3 py-2 border border-line rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-accent text-sm"
              >
                <option value="low">Low — General info</option>
                <option value="medium">Medium — Important update</option>
                <option value="high">High — Urgent notice</option>
              </select>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => setShowBroadcast(false)} disabled={sending}>Cancel</Button>
              <Button
                onClick={handleBroadcast}
                disabled={sending || !bTitle.trim() || !bContent.trim()}
                data-testid="broadcast-send-btn"
                className="flex-1 bg-ink hover:bg-ink-hover"
              >
                {sending ? 'Sending…' : 'Send broadcast'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Signup access control modal — code + pending approvals */}
      <AdminAccessControl open={showAccessControl} onClose={() => setShowAccessControl(false)} />
    </>
  );
}
